import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { 
  History, Search, Calendar, MapPin, User, Plane, 
  CheckCircle2, XCircle, ChevronRight, Download, RefreshCw, Archive
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

export const MissionHistory = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [missions, setMissions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterOrg, setFilterOrg] = useState("all");
  const [filterMonth, setFilterMonth] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [missionsRes, orgsRes, pilotsRes, aircraftRes] = await Promise.all([
        api.get('/missions'),
        api.get('/organizations'),
        api.get('/pilots'),
        api.get('/aircraft')
      ]);
      
      // Filter to only completed/past missions
      const completedMissions = missionsRes.data.filter(m => {
        const isCompleted = m.lifecycle_status === "completed" || m.completed_at;
        const isPast = new Date(m.mission_date) < new Date(new Date().toDateString());
        return isCompleted || isPast;
      });
      
      setMissions(completedMissions);
      setOrganizations(orgsRes.data);
      setPilots(pilotsRes.data);
      setAircraft(aircraftRes.data);
    } catch (error) {
      toast.error(t.messages?.fetchError || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [t.messages?.fetchError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getPilotName = (pilotId) => {
    const pilot = pilots.find(p => p.id === pilotId);
    return pilot?.name || "-";
  };

  const getAircraftModel = (aircraftId) => {
    const ac = aircraft.find(a => a.id === aircraftId);
    return ac?.model || "-";
  };

  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.name || "-";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  // Generate month options for filter
  const getMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: d.toLocaleDateString(t.lang === 'cs' ? 'cs-CZ' : 'en-US', { month: 'long', year: 'numeric' })
      });
    }
    return months;
  };

  const filteredMissions = missions.filter(mission => {
    const matchesSearch = 
      mission.mission_id?.toLowerCase().includes(search.toLowerCase()) ||
      mission.location?.toLowerCase().includes(search.toLowerCase()) ||
      getPilotName(mission.pilot_id).toLowerCase().includes(search.toLowerCase());
    
    const matchesOrg = filterOrg === "all" || mission.organization_id === filterOrg;
    
    const matchesMonth = filterMonth === "all" || 
      mission.mission_date?.startsWith(filterMonth);
    
    return matchesSearch && matchesOrg && matchesMonth;
  });

  const exportCSV = () => {
    const headers = ["Mission ID", "Date", "Location", "Pilot", "Aircraft", "Compliant"];
    const rows = filteredMissions.map(m => [
      m.mission_id,
      formatDate(m.mission_date),
      m.location,
      getPilotName(m.pilot_id),
      getAircraftModel(m.aircraft_id),
      m.compliance_flag ? "Yes" : "No"
    ]);
    
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    // UTF-8 BOM prefix so Excel on Windows reads diacritics correctly instead of guessing the
    // system codepage and rendering mojibake (e.g. "Tomáš Dvořák" -> "TomĂˇĹˇ DvoĹ™Ăˇk") — same fix
    // already applied on the .NET side's CSV export (CsvUtils.ToUtf8CsvBytes), missing here because
    // this export is built entirely client-side instead of calling the backend endpoint.
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mission-history-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="mission-history-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            {t.missionHistory?.title || "Mission History"}
          </h1>
          <p className="text-slate-500 mt-1">
            {t.missionHistory?.subtitle || "View completed and past missions"}
          </p>
        </div>
        <Button onClick={exportCSV} variant="outline" className="gap-2">
          <Download className="h-4 w-4" />
          {t.actions?.export || "Export CSV"}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={t.missionHistory?.searchPlaceholder || "Search missions..."}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="history-search"
                />
              </div>
            </div>
            <Select value={filterMonth} onValueChange={setFilterMonth}>
              <SelectTrigger className="w-[200px]" data-testid="history-month-filter">
                <SelectValue placeholder={t.missionHistory?.filterByMonth || "Filter by month"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.common?.all || "All Months"}</SelectItem>
                {getMonthOptions().map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {organizations.length > 1 && (
              <Select value={filterOrg} onValueChange={setFilterOrg}>
                <SelectTrigger className="w-[200px]" data-testid="history-org-filter">
                  <SelectValue placeholder={t.missionHistory?.filterByOrg || "Filter by organization"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.common?.all || "All Organizations"}</SelectItem>
                  {organizations.map(org => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {filteredMissions.length} {t.missionHistory?.missionsFound || "missions found"}
        </p>
      </div>

      {/* Missions Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.flightOps?.missionId || "Mission ID"}</TableHead>
                <TableHead>{t.flightOps?.missionDate || "Date"}</TableHead>
                <TableHead>{t.flightOps?.location || "Location"}</TableHead>
                <TableHead>{t.flightOps?.pilot || "Pilot"}</TableHead>
                <TableHead>{t.flightOps?.aircraft || "Aircraft"}</TableHead>
                <TableHead>{t.flightOps?.compliance || "Compliance"}</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <History className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">{t.missionHistory?.noHistory || "No mission history found"}</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredMissions.map((mission) => (
                  <TableRow 
                    key={mission.id} 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/missions/${mission.id}`)}
                    data-testid={`history-mission-${mission.id}`}
                  >
                    <TableCell>
                      <span className="font-medium text-blue-600 hover:underline">
                        {mission.mission_id}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-slate-400" />
                        {formatDate(mission.mission_date)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        {mission.location}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-slate-400" />
                        {getPilotName(mission.pilot_id)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Plane className="h-4 w-4 text-slate-400" />
                        {getAircraftModel(mission.aircraft_id)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {/* Historical missions show "Archived" badge instead of compliance status */}
                      <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                        <Archive className="h-3 w-3 mr-1" />
                        {t.missionStatus?.archived || "Archived"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default MissionHistory;
